import React, { useState, useEffect } from 'react';
import { FiX, FiUser, FiMail, FiLock, FiClock, FiAlertTriangle } from 'react-icons/fi';
import { usersAPI } from '../services/api';
import { useQueryClient, useQuery } from 'react-query';

const UserManagementModal = ({ user, isOpen, onClose }) => {
  const queryClient = useQueryClient();

  // ESC key handler
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);
  
  // Fetch full user data with email and profile
  const { data: fullUserData, isLoading: loadingUserData, error: userDataError } = useQuery(
    ['user-full', user?.id],
    async () => {
      console.log('API call triggered for user ID:', user?.id);
      if (!user) {
        console.log('No user provided, skipping API call');
        return null;
      }
      
      try {
        const response = await usersAPI.getUserById(user.id);
        console.log('Raw getUserById response:', response);
        console.log('Response keys:', Object.keys(response || {}));
        console.log('Response.data:', response.data);
        console.log('Response.data keys:', Object.keys(response.data || {}));
        console.log('Response.data.user:', response.data.user);
        console.log('Response.data.user.email:', response.data.user?.email);
        return response;
      } catch (error) {
        console.error('getUserById error:', error);
        throw error;
      }
    },
    { 
      enabled: !!user?.id && isOpen,
      cacheTime: 0,
      staleTime: 0,
      refetchOnMount: 'always',
      onSuccess: (data) => {
        console.log('Query onSuccess - Raw API response:', data);
      },
      onError: (error) => {
        console.error('Query onError - Failed to fetch full user data:', error);
      }
    }
  );
  
  console.log('Query state:', { 
    isLoading: loadingUserData, 
    error: userDataError, 
    hasData: !!fullUserData,
    enabled: !!user?.id && isOpen 
  });
  
  // Use full data if available, otherwise fall back to provided user
  // Fix: The API response is an axios response, so we need response.data.user
  const userData = fullUserData?.data?.user || user;
  
  console.log('UserData resolution:', {
    hasFullUserData: !!fullUserData,
    fullUserDataKeys: fullUserData ? Object.keys(fullUserData) : [],
    hasDataProperty: !!fullUserData?.data,
    dataKeys: fullUserData?.data ? Object.keys(fullUserData.data) : [],
    hasUserInData: !!fullUserData?.data?.user,
    userDataEmail: fullUserData?.data?.user?.email,
    finalUserData: userData,
    finalEmail: userData?.email
  });
  
  // Extract first and last name from profile or user data
  const getFirstName = (userData) => {
    if (!userData) return '';
    
    // First check direct firstName field
    if (userData.firstName) return userData.firstName;
    
    // Then check profile object
    if (userData.profile) {
      let profile = userData.profile;
      // Parse if it's a string
      if (typeof profile === 'string') {
        try {
          profile = JSON.parse(profile);
        } catch (e) {
          profile = {};
        }
      }
      if (profile.firstName) return profile.firstName;
    }
    
    return '';
  };
  
  const getLastName = (userData) => {
    if (!userData) return '';
    
    // First check direct lastName field
    if (userData.lastName) return userData.lastName;
    
    // Then check profile object
    if (userData.profile) {
      let profile = userData.profile;
      // Parse if it's a string
      if (typeof profile === 'string') {
        try {
          profile = JSON.parse(profile);
        } catch (e) {
          profile = {};
        }
      }
      if (profile.lastName) return profile.lastName;
    }
    
    return '';
  };
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    isActive: true
  });
  const [banDuration, setBanDuration] = useState('');
  const [banReason, setBanReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('edit'); // 'edit', 'ban', 'delete'

  // Reset and initialize form when modal opens with new user data
  useEffect(() => {
    if (isOpen && userData) {
      console.log('UserManagementModal - userData received:', userData);
      console.log('Email from userData:', userData.email);
      console.log('Username from userData:', userData.username);
      
      setFormData({
        username: userData.username || '',
        email: userData.email || '',
        firstName: getFirstName(userData),
        lastName: getLastName(userData),
        isActive: userData.isActive !== false
      });
      setBanDuration('');
      setBanReason('');
      setError('');
      setSuccess('');
      setActiveTab('edit');
    }
  }, [isOpen, userData]);

  if (!isOpen || !user) return null;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    console.log('Input changed:', name, type === 'checkbox' ? checked : value);
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };
      console.log('New form data:', newData);
      return newData;
    });
  };

  const handleUpdate = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await usersAPI.adminUpdateUser(user.id, formData);
      setSuccess('User updated successfully');
      queryClient.invalidateQueries('users');
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleBan = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await usersAPI.banUser(user.id, {
        duration: banDuration ? parseInt(banDuration) : null,
        reason: banReason
      });
      setSuccess(`User ${banDuration ? `banned for ${banDuration} hours` : 'banned permanently'}`);
      queryClient.invalidateQueries('users');
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to ban user');
    } finally {
      setLoading(false);
    }
  };

  const handleUnban = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await usersAPI.unbanUser(user.id);
      setSuccess('User unbanned successfully');
      queryClient.invalidateQueries('users');
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to unban user');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!userData || !userData.id) {
      setError('User data not available');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete user "${userData.username || userData.displayName}"? This action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await usersAPI.deleteUser(userData.id);
      setSuccess('User deleted successfully');
      queryClient.invalidateQueries('users');
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gray-50 px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            Manage User: {userData?.displayName || userData?.username || 'Loading...'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('edit')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'edit'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <FiUser className="inline-block mr-2" />
            Edit User
          </button>
          <button
            onClick={() => setActiveTab('ban')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'ban'
                ? 'text-orange-600 border-b-2 border-orange-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <FiLock className="inline-block mr-2" />
            Ban/Unban
          </button>
          <button
            onClick={() => setActiveTab('delete')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'delete'
                ? 'text-red-600 border-b-2 border-red-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <FiAlertTriangle className="inline-block mr-2" />
            Delete
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Success/Error Messages */}
          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg">
              {success}
            </div>
          )}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          {/* Edit Tab */}
          {activeTab === 'edit' && (
            <div className="space-y-4">
              {loadingUserData ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-2">Loading user data...</p>
                </div>
              ) : (
                <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Username
                </label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <FiMail className="inline mr-1" />
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="isActive"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                  Account Active
                </label>
              </div>

              <button
                onClick={handleUpdate}
                disabled={loading}
                className="btn btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Updating...' : 'Update User'}
              </button>
                </>
              )}
            </div>
          )}

          {/* Ban Tab */}
          {activeTab === 'ban' && (
            <div className="space-y-4">
              {user.isActive === false || user.bannedUntil ? (
                <div>
                  <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                    <p className="text-orange-700 font-medium">User is currently banned</p>
                    {user.bannedUntil && (
                      <p className="text-sm text-orange-600 mt-1">
                        Until: {new Date(user.bannedUntil).toLocaleString()}
                      </p>
                    )}
                    {user.banReason && (
                      <p className="text-sm text-orange-600 mt-1">
                        Reason: {user.banReason}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={handleUnban}
                    disabled={loading}
                    className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? 'Processing...' : 'Unban User'}
                  </button>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <FiClock className="inline mr-1" />
                      Ban Duration (hours, leave empty for permanent)
                    </label>
                    <input
                      type="number"
                      value={banDuration}
                      onChange={(e) => setBanDuration(e.target.value)}
                      placeholder="e.g., 24 for 1 day, 168 for 1 week"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ban Reason (optional)
                    </label>
                    <textarea
                      value={banReason}
                      onChange={(e) => setBanReason(e.target.value)}
                      placeholder="Enter reason for ban..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>

                  <button
                    onClick={handleBan}
                    disabled={loading}
                    className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? 'Processing...' : banDuration ? `Ban for ${banDuration} hours` : 'Ban Permanently'}
                  </button>
                </>
              )}
            </div>
          )}

          {/* Delete Tab */}
          {activeTab === 'delete' && (
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 font-medium mb-2">
                  <FiAlertTriangle className="inline mr-2" />
                  Warning: This action cannot be undone
                </p>
                <p className="text-sm text-red-600">
                  Deleting this user will:
                </p>
                <ul className="list-disc list-inside text-sm text-red-600 mt-2">
                  <li>Remove their account and all personal data</li>
                  <li>Keep their achievements for historical records</li>
                  <li>Prevent them from logging in</li>
                </ul>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">User to delete:</p>
                <p className="font-medium text-gray-900">{userData?.displayName || userData?.username}</p>
                <p className="text-sm text-gray-600">{userData?.email}</p>
              </div>

              <button
                onClick={handleDelete}
                disabled={loading}
                className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Deleting...' : 'Delete User Permanently'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserManagementModal;